/**
 * User Datasource (Infrastructure)
 *
 * Handles raw API communication for user operations.
 */

import { apiClient } from '@/shared/api';

import type { User, UserApiResponse, UserResponse, TestCallResponse, ClearCacheResponse } from '../../domain/types';
import { UserRole, type UserId } from '../../domain/types';

// ============================================================================
// Datasource Interface
// ============================================================================

export interface UserDatasource {
  /**
   * Get current user profile
   * Calls: GET /user
   */
  getCurrentUser(): Promise<UserResponse>;

  /**
   * Update user profile
   */
  updateProfile(data: Partial<User>): Promise<UserResponse>;

  /**
   * Create a test call
   * Calls: POST /user/test-call
   */
  createTestCall(): Promise<TestCallResponse>;

  /**
   * End a test call
   * Calls: DELETE /user/test-call?channel_id=<id>
   */
  endTestCall(channelId: string): Promise<void>;

  /**
   * Clear cache for the current user
   * Calls: POST /cache
   */
  clearUserCache(): Promise<ClearCacheResponse>;
}

// ============================================================================
// Helper: Transform API response to User entity
// ============================================================================

function mapApiResponseToUser(apiResponse: UserApiResponse): User {
  return {
    id: apiResponse.authenticatedId as UserId,
    authenticatedId: apiResponse.authenticatedId,
    email: apiResponse.email,
    name: apiResponse.displayName,
    displayName: apiResponse.displayName,
    avatar: apiResponse.photo_url,
    photoUrl: apiResponse.photo_url,
    role: (apiResponse.role as UserRole) || UserRole.Agent,
    extensionNumber: apiResponse.extension?.toString(),
    extensionStatus: apiResponse.extension_status,
    extensionType: apiResponse.extension_type,
    mobileNumber: apiResponse.mobileNumber,
    status: apiResponse.status,
    sipPasswordEncrypted: apiResponse.sip_password,
  };
}

// ============================================================================
// Datasource Implementation
// ============================================================================

export class UserDatasourceImpl implements UserDatasource {
  async getCurrentUser(): Promise<UserResponse> {
    const response = await apiClient.get<UserApiResponse>('/app/me');
    return {
      user: mapApiResponseToUser(response.data),
    };
  }

  async updateProfile(data: Partial<User>): Promise<UserResponse> {
    const response = await apiClient.put<UserApiResponse>('/app/me', data);
    return {
      user: mapApiResponseToUser(response.data),
    };
  }

  async createTestCall(): Promise<TestCallResponse> {
    const response = await apiClient.post<TestCallResponse>('/user/test-call');
    return response.data;
  }

  async endTestCall(channelId: string): Promise<void> {
    await apiClient.delete('/user/test-call', { params: { channel_id: channelId } });
  }

  async clearUserCache(): Promise<ClearCacheResponse> {
    const response = await apiClient.post<ClearCacheResponse>('/cache', { action: 'clear_user' });
    return response.data;
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

export const userDatasource: UserDatasource = new UserDatasourceImpl();
